/**
 * Redesigned Manage Returns list page shell: tab navigation, filters column, search/export row, and RMA data table.
 */
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import usePage from "../../../components/layouts/base-page-layout/page-hook";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardTranslationsStore from "../../../dashboard-translation/stores/dashboard-translations-store";
import uiSettingsStore from "../../../stores/ui-settings-store";
import rmsMixpanelStore from "../../../stores/rms-mixpanel-store";
import rmaPagedListStore from "./stores/rma-tabs-paged-list-store";
import showSbrPageStore from "../rma-list-page/stores/show-sbr-page.store";
import RmaListFiltersTabletDrawer from "./components/rma-list-filters-tablet-drawer/rma-list-filters-tablet-drawer";
import RmaListFiltersSidebar from "./components/rma-list-filters-sidebar/rma-list-filters-sidebar";
import RmaListMainToolbar from "./components/rma-list-main-toolbar/rma-list-main-toolbar";
import RmaListTable from "./components/rma-list-table/rma-list-table";
import RmaListTopBar from "./components/rma-list-top-bar/rma-list-top-bar";
import "./rma-list-page-new.scss";
import translationStore from "../../../translation/stores/translations-store";

const RmaListPageNew: React.FC = observer(() => {
    const rmaListFiltersStore = rmaPagedListStore.rmaListFiltersStore;
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const isLoading =
        rmaPagedListStore.isLoading ||
        dashboardTranslationsStore.isLoading ||
        rmaListFiltersStore.isLoading ||
        uiSettingsStore.isLoading ||
        uiSettingsStore.isSaving() ||
        translationStore.loadScope('resolutions').isLoading;

    const translatedSubtitle = getTranslation("MANAGE_RETURNS_SUB_TITLE", scope);
    const { setInstruction, setSubTitles, setHasBack } = usePage();
    const [showTabletFiltersDrawer, setShowTabletFiltersDrawer] = useState(false);

    useEffect(() => {
        setSubTitles(undefined);
        setHasBack(false);
    }, [setSubTitles, setHasBack]);

    useEffect(() => {
        setInstruction(translatedSubtitle);
    }, [translatedSubtitle, setInstruction]);

    useEffect(() => {
        translationStore.loadScope('resolutions');
        rmaPagedListStore.load();
        rmsMixpanelStore.load();
        showSbrPageStore.fetchShowSBRPageOnce();
    }, []);

    useEffect(() => {
        rmaListFiltersStore.loadFilterOptions();
    }, [rmaListFiltersStore]);

    const onSearchClick = useCallback(() => {
        rmsMixpanelStore.trackEvent("d&_search_rma_input", { input: rmaListFiltersStore.searchInput });
        rmaPagedListStore.loadPage(1);
    }, [rmaListFiltersStore]);

    const onSearchInputChange = useCallback(
        (value: string) => {
            rmaListFiltersStore.setSearchCustomerId(value);
        },
        [rmaListFiltersStore],
    );

    const onSearchExactMatchChange = useCallback(() => {
        rmaListFiltersStore.toggleSearchExactMatch();
        if (rmaListFiltersStore.searchInput) {
            rmaPagedListStore.loadPage(1);
        }
    }, [rmaListFiltersStore]);

    return (
        <>
            <div className="rma-list-page-new">
                <RmaListTopBar isLoading={isLoading} />
                <div className="rma-list-page-new__columns">
                    <div className="rma-list-page-new__sidebar">
                        <RmaListFiltersSidebar rmaListFiltersStore={rmaListFiltersStore} disabled={isLoading} />
                    </div>
                    <div className="rma-list-page-new__main">
                        <RmaListMainToolbar
                            rmaListFiltersStore={rmaListFiltersStore}
                            isLoading={isLoading}
                            onSearchClick={onSearchClick}
                            onSearchInputChange={onSearchInputChange}
                            onSearchExactMatchChange={onSearchExactMatchChange}
                            onTabletFiltersOpen={() => setShowTabletFiltersDrawer(true)}
                        />
                        <RmaListTable />
                    </div>
                </div>
            </div>

            {showTabletFiltersDrawer && (
                <RmaListFiltersTabletDrawer
                    rmaListFiltersStore={rmaListFiltersStore}
                    disabled={isLoading}
                    onClose={() => setShowTabletFiltersDrawer(false)}
                />
            )}
        </>
    );
});

export default RmaListPageNew;
